// Detects and recovers from "session swaps".
//
// The app authenticates via an HTTP-only session cookie. All Chrome incognito
// windows (and all tabs of a normal profile) share one cookie jar, so signing in
// as a second account in another window silently overwrites the cookie the first
// window is still using. The first window then keeps showing the original user's UI
// but sends the *other* user's cookie, producing confusing 403s on the next action.
//
// This module compares the identity the backend sees (/api/auth/check) with the
// identity this tab believes it is showing, and forces a clean re-login when they
// diverge — turning a cryptic error into an explained "please log in again".

import { fetchUserInfo } from "../services/userService";
import { forceReauth, getUserId } from "./auth";

export type SessionState = "ok" | "unauthenticated" | "mismatch";

/**
 * Compares the backend session identity with the identity this tab expects.
 *
 * @param expectedUserId the numeric user id this tab expects. When omitted, falls
 *   back to the in-memory auth snapshot (which is per-tab and therefore NOT clobbered
 *   when another window overwrites the shared localStorage/cookie).
 * @returns
 *   - "ok": the session belongs to the expected user.
 *   - "mismatch": the session belongs to a *different* authenticated user (swap).
 *   - "unauthenticated": no valid session (expired) — or the check could not be
 *     completed (network error), since {@link fetchUserInfo} cannot distinguish the
 *     two. Callers that must avoid false positives should act only on "mismatch".
 */
export async function verifySessionIdentity(
    expectedUserId?: string | number | null,
): Promise<SessionState> {
    const expected = expectedUserId != null ? String(expectedUserId) : getUserId();
    const info = await fetchUserInfo();

    if (!info || !info.authenticated) {
        return "unauthenticated";
    }
    if (expected != null && info.id != null && String(info.id) !== expected) {
        return "mismatch";
    }
    return "ok";
}

/**
 * Call after an authenticated request unexpectedly returns 401/403. Distinguishes a
 * genuine permission error (same user, valid session — the caller should surface its
 * normal message) from a swapped or expired session (force a clean re-login).
 *
 * Because this only runs after a real HTTP response was received, the network is
 * known to be working, so treating a null auth-check as "unauthenticated" is safe.
 *
 * @returns true if it triggered a redirect; the caller should then stop handling the
 *   error (a hard navigation is already in progress).
 */
export async function enforceSessionOnAuthError(
    status: number,
    expectedUserId?: string | number | null,
): Promise<boolean> {
    if (status !== 401 && status !== 403) {
        return false;
    }
    const state = await verifySessionIdentity(expectedUserId);
    if (state === "unauthenticated") {
        forceReauth("session_expired");
        return true;
    }
    if (state === "mismatch") {
        forceReauth("session_changed");
        return true;
    }
    return false;
}
